import { useState, useEffect } from 'react';
import { Clock, Calendar, Save, Loader2, DollarSign } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../lib/api';

const WEEKDAYS = [
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
    { value: 7, label: 'Sun' },
];

const SLOT_OPTIONS = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 60, label: '60 min' },
];

export const SUPPORTED_TIMEZONES = [
    { value: 'Pacific/Honolulu', label: 'Hawaii (HST) – Honolulu' },
    { value: 'America/Anchorage', label: 'Alaska (AKST) – Anchorage' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT) – Los Angeles' },
    { value: 'America/Vancouver', label: 'Pacific Time (PT) – Vancouver' },
    { value: 'America/Phoenix', label: 'Mountain Standard (MST) – Phoenix' },
    { value: 'America/Denver', label: 'Mountain Time (MT) – Denver' },
    { value: 'America/Edmonton', label: 'Mountain Time (MT) – Edmonton' },
    { value: 'America/Chicago', label: 'Central Time (CT) – Chicago' },
    { value: 'America/Winnipeg', label: 'Central Time (CT) – Winnipeg' },
    { value: 'America/New_York', label: 'Eastern Time (ET) – New York' },
    { value: 'America/Toronto', label: 'Eastern Time (ET) – Toronto' },
    { value: 'America/Halifax', label: 'Atlantic Time (AT) – Halifax' },
    { value: 'UTC', label: 'UTC' },
] as const;

interface Props {
    warehouseId: string;
    initialSchedule?: {
        timeZone?: string;
        operatingDays?: number[];
        openTime?: string;
        closeTime?: string;
        slotMinutes?: number;
        detentionGraceMinutes?: number;
        detentionRate?: number;
    };
    hideTimezoneSelector?: boolean;
    onTimeZoneChange?: (tz: string) => void;
    onSaved?: () => void;
}

export function WarehouseScheduleSettings({ warehouseId, initialSchedule, hideTimezoneSelector = false, onTimeZoneChange, onSaved }: Props) {
    const [timeZone, setTimeZone] = useState(initialSchedule?.timeZone ?? 'America/Los_Angeles');
    const [operatingDays, setOperatingDays] = useState<number[]>(initialSchedule?.operatingDays ?? [1, 2, 3, 4, 5]);
    const [openTime, setOpenTime] = useState(initialSchedule?.openTime ?? '08:00');
    const [closeTime, setCloseTime] = useState(initialSchedule?.closeTime ?? '17:00');
    const [allowOvernight, setAllowOvernight] = useState<boolean>(
        Boolean(initialSchedule?.openTime && initialSchedule?.closeTime && initialSchedule.closeTime < initialSchedule.openTime)
    );
    const [slotMinutes, setSlotMinutes] = useState(initialSchedule?.slotMinutes ?? 60);
    const [detentionGraceMinutes, setDetentionGraceMinutes] = useState(initialSchedule?.detentionGraceMinutes ?? 60);
    const [detentionRate, setDetentionRate] = useState(initialSchedule?.detentionRate ?? 75);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (initialSchedule) {
            if (initialSchedule.timeZone) setTimeZone(initialSchedule.timeZone);
            if (initialSchedule.operatingDays) setOperatingDays(initialSchedule.operatingDays);
            if (initialSchedule.openTime) setOpenTime(initialSchedule.openTime);
            if (initialSchedule.closeTime) setCloseTime(initialSchedule.closeTime);
            if (initialSchedule.openTime && initialSchedule.closeTime) {
                setAllowOvernight(initialSchedule.closeTime < initialSchedule.openTime);
            }
            if (initialSchedule.slotMinutes) setSlotMinutes(initialSchedule.slotMinutes);
            if (initialSchedule.detentionGraceMinutes !== undefined) setDetentionGraceMinutes(initialSchedule.detentionGraceMinutes);
            if (initialSchedule.detentionRate !== undefined) setDetentionRate(initialSchedule.detentionRate);
        }
    }, [initialSchedule]);

    const toggleDay = (day: number) => {
        setOperatingDays(prev =>
            prev.includes(day)
                ? prev.filter(d => d !== day)
                : [...prev, day].sort()
        );
    };

    const handleSave = async () => {
        if (openTime === closeTime) {
            toast.error('Open and close times cannot be the same');
            return;
        }
        if (closeTime < openTime && !allowOvernight) {
            toast.error('Close time is earlier than open time. Enable "Overnight shift (closes next day)" if this facility runs overnight, or choose a close time after open time.');
            return;
        }
        if (operatingDays.length === 0) {
            toast.error('At least one operating day required');
            return;
        }

        setSaving(true);
        try {
            await api.updateWarehouse(warehouseId, {
                timeZone,
                operatingDays,
                openTime,
                closeTime,
                slotMinutes,
                detentionGraceMinutes,
                detentionRate,
            });
            toast.success('Operating hours saved');
            onSaved?.();
        } catch (err: any) {
            toast.error(err.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card p-6 border-l-4 border-l-orange-500">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                <Clock className="text-orange-500" /> Operating Hours
            </h2>

            {/* Timezone */}
            {!hideTimezoneSelector && (
                <div className="mb-6">
                    <label className="block text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        Timezone
                    </label>
                    <select
                        value={timeZone}
                        onChange={(e) => {
                            setTimeZone(e.target.value);
                            onTimeZoneChange?.(e.target.value);
                        }}
                        className="field w-full max-w-xs"
                    >
                        {SUPPORTED_TIMEZONES.map(tz => (
                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Operating Days */}
            <div className="mb-6">
                <label className="block text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    Operating Days
                </label>
                <div className="flex gap-2 flex-wrap">
                    {WEEKDAYS.map(day => (
                        <button
                            key={day.value}
                            type="button"
                            onClick={() => toggleDay(day.value)}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${operatingDays.includes(day.value)
                                ? 'bg-orange-500/20 border-orange-500 text-orange-600 dark:text-orange-400'
                                : 'bg-muted/20 border-border text-muted-foreground hover:border-orange-500/50'
                                }`}
                        >
                            {day.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Open / Close / Slot */}
            <div className="grid md:grid-cols-3 gap-6 mb-4">
                <div>
                    <label className="block text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        Open Time
                    </label>
                    <input
                        type="time"
                        value={openTime}
                        onChange={(e) => setOpenTime(e.target.value)}
                        className="field w-full"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        Close Time
                    </label>
                    <input
                        type="time"
                        value={closeTime}
                        onChange={(e) => setCloseTime(e.target.value)}
                        className="field w-full"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        Slot Duration
                    </label>
                    <div className="flex bg-muted rounded-lg p-1 border border-border">
                        {SLOT_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setSlotMinutes(opt.value)}
                                className={`flex-1 py-1.5 rounded-md text-sm font-bold transition-all ${slotMinutes === opt.value
                                    ? 'bg-white dark:bg-background shadow-sm text-foreground border border-slate-200 dark:border-transparent'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {closeTime < openTime && (
                <div className="mb-6 p-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/30 flex items-center gap-3">
                    <input
                        id="overnight-shift-toggle"
                        type="checkbox"
                        checked={allowOvernight}
                        onChange={(e) => setAllowOvernight(e.target.checked)}
                        className="w-4 h-4 rounded border-amber-400 text-orange-600 focus:ring-orange-500"
                    />
                    <label htmlFor="overnight-shift-toggle" className="text-xs font-medium text-amber-900 dark:text-amber-200 cursor-pointer">
                        Overnight shift (closes next day at {closeTime}). Check this box to confirm intentional overnight operating hours.
                    </label>
                </div>
            )}

            {/* Detention & Grace (server-authoritative) */}
            <div className="mb-6">
                <label className="block text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                    <DollarSign className="inline w-4 h-4 mr-1" /> Detention & Grace
                </label>
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">Detention Free Time (Minutes)</label>
                        <input
                            type="number"
                            min={0}
                            max={180}
                            value={detentionGraceMinutes}
                            onChange={(e) => setDetentionGraceMinutes(Math.min(180, Math.max(0, parseInt(e.target.value) || 0)))}
                            className="field w-full"
                        />
                        <p className="text-xs text-muted-foreground mt-1">0–180 mins. Dwell under this is not detention.</p>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1">Detention Rate ($/hr)</label>
                        <input
                            type="number"
                            min={0}
                            max={500}
                            value={detentionRate}
                            onChange={(e) => setDetentionRate(Math.min(500, Math.max(0, parseFloat(e.target.value) || 0)))}
                            className="field w-full"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Used for detention cost estimates in analytics.</p>
                    </div>
                </div>
            </div>

            {/* Preview */}
            <div className="bg-slate-50 dark:bg-muted/30 p-4 rounded-lg border border-border mb-6">
                <p className="text-sm text-muted-foreground">
                    <Calendar className="inline w-4 h-4 mr-1" />
                    <strong>{operatingDays.map(d => WEEKDAYS.find(w => w.value === d)?.label).join(', ')}</strong>
                    {' · '}
                    {openTime} – {closeTime}{closeTime < openTime ? ' (overnight)' : ''}
                    {' · '}
                    {slotMinutes}-min slots
                    {' · '}
                    {timeZone.replace('America/', '').replace('Pacific/', '').replace(/_/g, ' ')}
                    {' · '}
                    Grace: {detentionGraceMinutes}m · Rate: ${detentionRate}/hr
                </p>
            </div>

            {/* Save */}
            <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary flex items-center gap-2"
            >
                {saving ? (
                    <><Loader2 className="animate-spin w-4 h-4" /> Saving...</>
                ) : (
                    <><Save className="w-4 h-4" /> Save Operating Hours</>
                )}
            </button>
        </div>
    );
}

export default WarehouseScheduleSettings;
